import React from 'react';
import Box from '@mui/material/Box';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, SubHead, InfoBox, WarnBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'subscription',    label: 'Subscription Model' },
  { id: 'free-trial',      label: 'Free Trial' },
  { id: 'eligibility',     label: 'Refund Eligibility' },
  { id: 'how-to-request',  label: 'How to Request a Refund' },
  { id: 'cancellation',    label: 'Cancellation' },
  { id: 'plan-changes',    label: 'Plan Upgrades & Downgrades' },
  { id: 'failed-payments', label: 'Failed Payments' },
  { id: 'taxes',           label: 'Taxes & Fees' },
  { id: 'disputes',        label: 'Dispute Resolution' },
  { id: 'contact',         label: 'Contact Billing' },
];

const related = [
  { label: 'Terms of Service', href: '/terms-of-service' },
  { label: 'SLA',              href: '/sla' },
  { label: 'Privacy Policy',   href: '/privacy-policy' },
];

interface MatrixRowProps {
  scenario: string;
  monthly: string;
  annual: string;
  enterprise: string;
}

const MatrixRow: React.FC<MatrixRowProps & { header?: boolean }> = ({
  scenario, monthly, annual, enterprise, header,
}) => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '2.5fr 1fr 1fr 1fr',
    borderBottom: '1px solid #e5e7eb',
    background: header ? '#1f2937' : 'transparent',
    '&:hover': header ? {} : { background: '#f9fafb' },
  }}>
    {[scenario, monthly, annual, enterprise].map((cell, i) => (
      <Box key={i} sx={{
        px: 2, py: 1.5,
        fontSize: header ? '0.78rem' : '0.875rem',
        color: header ? '#f9fafb' : '#374151',
        fontWeight: header ? 700 : (i === 0 ? 500 : 400),
        textTransform: header ? 'uppercase' : 'none',
        letterSpacing: header ? '0.04em' : 'normal',
      }}>
        {cell}
      </Box>
    ))}
  </Box>
);

const RefundPolicyPage: React.FC = () => (
  <PolicyPageLayout title="Refund Policy" lastUpdated="April 26, 2026" toc={toc} related={related}>

    <PolicySection id="subscription" number="1" title="Subscription Model">
      <Para>
        CruvzMail offers email hosting on monthly and annual subscription plans. All subscriptions
        are prepaid — you pay for the period ahead. Payments are processed securely through
        Razorpay. By subscribing, you authorize recurring charges at the start of each billing cycle.
      </Para>
      <InfoBox>
        Annual subscriptions offer a discount of up to 20% compared to the equivalent monthly rate.
        Annual plans are billed as a single upfront payment for the full 12-month term.
      </InfoBox>
    </PolicySection>

    <PolicySection id="free-trial" number="2" title="Free Trial">
      <Para>
        New organizational accounts may be eligible for a free trial period at CruvzMail's discretion.
        During the free trial:
      </Para>
      <BulletList>
        <BulletItem>No payment information is required to start the trial</BulletItem>
        <BulletItem>Full Service features are available within trial usage limits</BulletItem>
        <BulletItem>The trial automatically ends at expiry — no automatic charge is applied</BulletItem>
        <BulletItem>To continue after the trial, you must select a paid plan and complete payment</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="eligibility" number="3" title="Refund Eligibility">
      <Para>
        Our refund eligibility depends on the subscription type and the reason for the refund request:
      </Para>
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', mt: 2, mb: 2 }}>
        <MatrixRow header scenario="Scenario" monthly="Monthly Plan" annual="Annual Plan" enterprise="Enterprise" />
        <MatrixRow scenario="Cancellation within 7 days of first payment" monthly="Full refund" annual="Full refund" enterprise="Contact sales" />
        <MatrixRow scenario="Service outage >24 continuous hours (our fault)" monthly="Pro-rated credit" annual="Pro-rated credit" enterprise="Per SLA terms" />
        <MatrixRow scenario="Duplicate charge (billing error)" monthly="Full refund" annual="Full refund" enterprise="Full refund" />
        <MatrixRow scenario="Cancellation after 7 days" monthly="No refund" annual="No refund" enterprise="Contact sales" />
        <MatrixRow scenario="Unused days remaining at cancellation" monthly="No refund" annual="No refund" enterprise="Contact sales" />
        <MatrixRow scenario="Account suspended for AUP violation" monthly="No refund" annual="No refund" enterprise="No refund" />
        <MatrixRow scenario="Plan downgrade mid-cycle" monthly="Credit to next cycle" annual="Pro-rated credit" enterprise="Contact sales" />
      </Box>
      <WarnBox>
        Accounts suspended or terminated for violations of our Acceptable Use Policy or Terms of
        Service are not eligible for refunds under any circumstances.
      </WarnBox>
    </PolicySection>

    <PolicySection id="how-to-request" number="4" title="How to Request a Refund">
      <Para>To request a refund, please follow these steps:</Para>
      <BulletList>
        <BulletItem>
          <strong>Step 1:</strong> Email billing@cruvzmail.com from the admin account email address
          associated with your subscription
        </BulletItem>
        <BulletItem>
          <strong>Step 2:</strong> Include your organization name, the payment date, the amount, and
          the Razorpay transaction ID (visible in your billing dashboard)
        </BulletItem>
        <BulletItem>
          <strong>Step 3:</strong> Describe the reason for the refund request
        </BulletItem>
        <BulletItem>
          <strong>Step 4:</strong> Our billing team will review your request within 3 business days
          and respond to your email
        </BulletItem>
      </BulletList>
      <SubHead>Processing Time</SubHead>
      <Para>
        Approved refunds are processed within 5–7 business days. The credit appears on your original
        payment method within 5–10 additional business days depending on your bank or card issuer.
      </Para>
    </PolicySection>

    <PolicySection id="cancellation" number="5" title="Cancellation">
      <Para>
        You may cancel your subscription at any time from the Admin Panel under Billing → Cancel
        Subscription. Upon cancellation:
      </Para>
      <BulletList>
        <BulletItem>
          Your account and all user accounts remain active until the end of your current paid period
        </BulletItem>
        <BulletItem>
          No further charges are applied after cancellation
        </BulletItem>
        <BulletItem>
          You can export your email data, contacts, and files before the period ends
        </BulletItem>
        <BulletItem>
          After your paid period expires, the account enters a 30-day grace period (read-only)
        </BulletItem>
        <BulletItem>
          All data is permanently deleted 30 days after the account enters the grace period
        </BulletItem>
      </BulletList>
      <Para>
        Cancellation does not entitle you to a refund for the remaining days in your current billing
        period unless you are within the 7-day new-customer refund window.
      </Para>
    </PolicySection>

    <PolicySection id="plan-changes" number="6" title="Plan Upgrades and Downgrades">
      <SubHead>Upgrades</SubHead>
      <Para>
        You can upgrade your plan at any time. The price difference between your current plan and the
        new plan is calculated on a pro-rated basis for the remaining days in your billing cycle and
        charged immediately.
      </Para>
      <SubHead>Downgrades</SubHead>
      <Para>
        Downgrades take effect at the start of your next billing cycle. If you downgrade an annual
        plan mid-cycle, a pro-rated credit for the difference will be applied to your account and
        deducted from future invoices. Cash refunds are not provided for plan downgrades.
      </Para>
    </PolicySection>

    <PolicySection id="failed-payments" number="7" title="Failed Payments">
      <Para>
        If a payment fails on your renewal date, CruvzMail will:
      </Para>
      <BulletList>
        <BulletItem>Immediately notify you by email with instructions to update your payment method</BulletItem>
        <BulletItem>Retry the payment automatically after 3 days</BulletItem>
        <BulletItem>Retry again after an additional 3 days</BulletItem>
        <BulletItem>
          If the third attempt fails, the account enters a 7-day grace period during which sending
          is restricted but receiving and accessing emails remains available
        </BulletItem>
        <BulletItem>
          After the grace period, the account is suspended until payment is completed
        </BulletItem>
      </BulletList>
      <Para>
        Suspended accounts due to non-payment can be reactivated at any time by completing payment.
        Data is retained for 30 days after suspension before permanent deletion.
      </Para>
    </PolicySection>

    <PolicySection id="taxes" number="8" title="Taxes and Fees">
      <Para>
        All subscription prices displayed exclude applicable taxes. Goods and Services Tax (GST) at
        the prevailing rate will be added to all invoices for customers in India. For international
        customers, local taxes may apply based on your jurisdiction.
      </Para>
      <Para>
        GST invoices are available and emailed automatically after each successful payment. You can
        also download all invoices from Admin Panel → Billing → Invoice History.
      </Para>
    </PolicySection>

    <PolicySection id="disputes" number="9" title="Dispute Resolution">
      <Para>
        If you believe you have been charged incorrectly, please contact billing@cruvzmail.com within
        60 days of the charge. We will investigate and respond within 5 business days.
      </Para>
      <Para>
        If you initiate a chargeback with your card provider without first contacting us, we reserve
        the right to immediately suspend the associated account pending resolution. Most billing
        disputes can be resolved quickly and amicably through direct communication.
      </Para>
    </PolicySection>

    <PolicySection id="contact" number="10" title="Contact Billing">
      <Para>For all billing inquiries, refund requests, and invoice issues:</Para>
      <ContactCard>
        <strong>Billing email:</strong> billing@cruvzmail.com<br />
        <strong>General support:</strong> support@cruvzmail.com<br />
        <br />
        Please include your organization name, account email, and transaction ID in all billing
        correspondence to help us assist you quickly.<br />
        <br />
        <strong>CruvzMail</strong> — operated by Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default RefundPolicyPage;
